import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { EventApiService } from '../../core/api-services/event-api.service';
import { EventDetail, EventSummary } from '../../core/models/event';

/**
 * Public events page.
 *
 * No account is needed: a visitor sees the events that are open to the public
 * and, when an event allows it, signs up with just a name and email address. An
 * administrator confirms the signup and the details follow by email.
 */
@Component({
  selector: 'app-public-events',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './events.html',
  styleUrl: './events.scss',
})
export class PublicEventsPage implements OnInit {
  private readonly api = inject(EventApiService);
  private readonly fb = inject(FormBuilder);

  readonly events = signal<EventSummary[]>([]);
  readonly selected = signal<EventDetail | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');

  readonly form = this.fb.group({
    fullName: ['', Validators.required],
    emailAddress: ['', [Validators.required, Validators.email]],
    sessionId: [null as number | null],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .getPublicEvents()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (events) => this.events.set(events ?? []),
        error: () => this.error.set('The events could not be loaded.'),
      });
  }

  open(event: EventSummary): void {
    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.form.reset({ fullName: '', emailAddress: '', sessionId: null });
    this.api
      .getPublicEvent(event.id)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => this.selected.set(detail),
        error: () => this.error.set('The event could not be opened.'),
      });
  }

  close(): void {
    this.selected.set(null);
  }

  submit(): void {
    const event = this.selected();
    if (!event || this.form.invalid || this.busy()) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.api
      .registerPublic(event.id, {
        fullName: value.fullName ?? '',
        emailAddress: value.emailAddress ?? '',
        sessionId: value.sessionId ?? null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.message.set(
            `Thanks ${value.fullName}. Your signup for ${event.title} is awaiting confirmation — we will email you.`
          );
          this.selected.set(null);
        },
        error: (err) => this.error.set(err?.error?.message || 'The signup could not be completed.'),
      });
  }

  canSignUp(event: EventDetail): boolean {
    return event.allowPublicSignup && event.registrationOpen;
  }
}
